import React from 'react';
import { makeStyles, Paper, CircularProgress, TableContainer, TableRow, TableCell, TableHead, Table, TableBody, Typography, Alert } from '~/lib/bn-material-ui';
import { sirenActions } from '~/utility/BinSentry-ui-utility';
import useSirenEntity from '~/utility/use-siren-entity';
import { BinLink } from '~/bins/bin-link';
import { useMillBinsGroup } from './use-mill-bins-group';
import { BinAvailableCapacity, BinLevel, BinLevelMeter, BinMaterial } from './bin-props';
import { useQuery } from './use-query';
import { useLocalSearchSort } from './use-local-search-sort';

const useTableStyles = makeStyles(theme => ({
  container: {
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tableContainer: {
    overflow: 'initial',
  },
  table: {
    borderCollapse: 'collapse',
    '& th, & td': {
      backgroundColor: 'white',
      padding: '0.5em 1em',
      textAlign: 'left',
      borderBottom: `1px solid ${theme.palette.gray[40]}`,
    },
    '& thead th': {
      boxShadow: '0px 2px 4px 0px rgba(0,0,0,0.1)',
      '& .MuiTypography-body2': {
        color: theme.palette.gray[60],
        fontWeight: 700,
      },
    },
    '& tbody tr:hover td': {
      backgroundColor: theme.palette.blue[10],
    },
  },
  meter: {
    width: '1px',
  },
  level: {
    '& .MuiTypography-body1': {
      fontWeight: 700,
    },
  },
  capacity: {
    '& .MuiTypography-body1': {
      fontWeight: 500,
      color: theme.palette.gray[70],
    },
  },
  bottom: {
    textAlign: 'center',
    padding: '1rem',
  },
  end: {
    color: theme.palette.gray[60],
    fontSize: '0.8em',
  },
}));

export default function InventoryTable() {
  const styles = useTableStyles();
  const { millBinsGroup, loading: loadingOrg } = useMillBinsGroup();

  const [{
    query = '',
    sort = 'name',
    order = 'asc',
  }] = useQuery();

  // Load all bins at once for local filtering
  const { entity: searchEntity } = useSirenEntity({ href: millBinsGroup?.getLink('https://api.binsentry.com/rel/search-bins')?.href });
  const searchAction = searchEntity?.getAction('search-bins');
  const url = searchAction && sirenActions.parseAction(searchAction, {
    query: '', // No query - load all bins
    sort: 'name', // Default sort
    order: 'asc', // Default order
    limit: 1000, // Load all bins at once for local filtering
  }).url;

  const { entity: binsEntity, loading: loadingBins } = useSirenEntity({ href: url?.href });
  const allBins = binsEntity?.getSubEntitiesByClass('physical-bin') || [];

  // Apply local search and sorting
  const { filteredBins: bins } = useLocalSearchSort(allBins, query, sort, order);


  if (loadingOrg) {
    return <div className={styles.bottom}><CircularProgress /></div>;
  }

  return (
    <div className={styles.container}>
      <TableContainer component={Paper} className={styles.tableContainer}>
        <Table className={styles.table} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><Typography variant='body2'>Bin Name</Typography></TableCell>
              <TableCell colSpan={2}><Typography variant='body2'>Current Level</Typography></TableCell>
              <TableCell><Typography variant='body2'>Available Capacity</Typography></TableCell>
              <TableCell><Typography variant='body2'>Material</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bins.map(binEntity => (
              <TableRow key={binEntity.getLink('self')?.href}>
                <TableCell><BinLink binHref={binEntity.getLink('self').href} /></TableCell>
                <TableCell className={styles.meter}><BinLevelMeter binEntity={binEntity} /></TableCell>
                <TableCell className={styles.level}><BinLevel binEntity={binEntity} /></TableCell>
                <TableCell className={styles.capacity}><BinAvailableCapacity binEntity={binEntity} /></TableCell>
                <TableCell><BinMaterial binEntity={binEntity} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles.bottom}>
        {loadingBins
          ? <CircularProgress />
          : <p className={styles.end}>~ end of list ~</p>}
      </div>
    </div>
  );
}
